import jwtAuthStore from "shared/store/jwtAuthStore";
import chatStore from "shared/store/chatStore";
import activityPanelStore from "shared/store/activityPanelStore";
import constraintsStore from "shared/store/constraintsStore";
import JwtApiService, { JwtVerifyResult } from "./JwtApiService";
import { CONSTRAINTS } from "pages/assistant/components/activity-panel/panels/constraints/constants";

/**
 * Service for handling JWT token-based authentication
 */
export const JwtAuthService = {
  /**
   * Verify a JWT token with the server
   * @param token JWT token to verify
   * @returns Promise resolving to an object with success flag and optional payload
   */
  verifyToken: async (token: string): Promise<JwtVerifyResult> => {
    try {
      console.info("JwtAuthService: Verifying token...");
      const { success, jwtSession, payload } = await JwtApiService.verifyToken(token);
      console.info("JwtAuthService: Token verification result:", success);

      // Update store based on verification result
      if (success) {
        console.info(
          "JwtAuthService: Token is valid, setting isVerified to true",
        );

        // Store the session ID for subsequent API calls
        if (jwtSession) {
          console.info("JwtAuthService: Storing jwtSession for API calls");
          jwtAuthStore.getState().setJwtSession(jwtSession);
        }

        jwtAuthStore.getState().setIsVerified(true);

        // Check if payload exists and trigger rebalancing chat
        if (payload) {
          console.info("JwtAuthService: Payload detected, checking for rebalancing data");
          console.log("JwtAuthService: Payload content:", payload);

          // Check if payload contains positions and customer_data fields for rebalancing
          // Use stricter checks to ensure the fields are not null or undefined
          // Optional chaining provides extra safety in case payload is not an object
          const hasRebalancingData =
            payload?.positions != null &&
            payload?.customer_data != null;

          if (hasRebalancingData) {
            console.info("JwtAuthService: Rebalancing data detected (positions + customer_data)");
            console.log("[JwtAuthService] Full payload:", JSON.stringify(payload, null, 2));

            // Determine mode from payload.feature: 'rebalancing' | 'diagnosis' | 'builder'
            const feature = payload?.feature as 'rebalancing' | 'diagnosis' | 'builder' | undefined;
            console.log("[JwtAuthService] payload.feature:", payload?.feature, "feature var:", feature);
            const mode = feature || 'builder';
            console.info("JwtAuthService: Feature mode from payload:", mode);

            // Rebalancing and diagnosis start their chat with an initial message.
            // Builder opens on a new chat with no turn: the advisor starts it.
            if (mode !== 'builder') {
              const message = mode === 'rebalancing'
                ? "Importa il portafoglio e visualizzane la composizione"
                : "Importa portafoglio ed esegui la diagnosi";

              chatStore.getState().setPendingInitialMessage({
                text: message,
                metadata: {
                  "fineco:fineco_imported_portfolio": payload,
                  [CONSTRAINTS.MODE_KEY]: { mode }
                }
              });
            }

            // Set constraints panel mode based on feature
            console.log('[JwtAuthService] Setting constraintsPanelMode to:', mode);
            activityPanelStore.getState().setConstraintsPanelMode(mode);
            console.log('[JwtAuthService] constraintsPanelMode after set:', activityPanelStore.getState().constraintsPanelMode);
            if (mode === 'builder' || mode === 'rebalancing') {
              constraintsStore.getState().setMode(mode);
            }

            // Store fee type from customer data (used for conditional score display)
            const feeType = payload?.customer_data?.feeType as 'feeOnTop' | 'feeOnly' | undefined;
            if (feeType) {
              activityPanelStore.getState().setFeeType(feeType);
              console.log('[JwtAuthService] feeType set to:', feeType);
            }
          } else {
            console.warn("JwtAuthService: Generic payload detected, but no rebalancing data found");
          }
        }

        return { success: true, payload };
      } else {
        console.info("JwtAuthService: Token is invalid, clearing auth");
        jwtAuthStore.getState().setIsVerified(false);
        jwtAuthStore.getState().clearJwtAuth();
        return { success: false };
      }
    } catch (error) {
      console.error("Error in JWT verification flow:", error);
      jwtAuthStore.getState().setIsVerified(false);
      jwtAuthStore.getState().clearJwtAuth();
      return { success: false };
    }
  },

  /**
   * Logout the user
   */
  logout: async () => {
    await JwtApiService.logout();
    jwtAuthStore.getState().clearJwtAuth();
  },

  ttl: async () => {
    const ttl = await JwtApiService.ttl();
    return ttl;
  },

  /**
   * Initialize JWT authentication from URL if available
   */
  initializeFromUrl: async (): Promise<void> => {
    // Initialize the store with URL data
    console.info("JwtAuthService: Initializing from URL");
    jwtAuthStore.getState().initializeFromUrl();

    // Get the extracted token and authentication state
    const { jwtToken, isInKnownHost, isVerified } = jwtAuthStore.getState();
    console.info(
      "JwtAuthService: Token status - URL token or stored token exists:",
      !!jwtToken,
      "isInKnownHost:",
      isInKnownHost,
    );

    // Verify the token:
    if (jwtToken && isInKnownHost && !isVerified) {
      console.info("JwtAuthService: Will verify token");
      await JwtAuthService.verifyToken(jwtToken);
    }
  },

  /**
   * Get the current JWT session if available (used for API calls)
   * Returns the session ID, not the original token
   */
  getToken: (): string | null => {
    const { jwtSession, shouldUseJwt } = jwtAuthStore.getState();
    return shouldUseJwt ? jwtSession : null;
  },

  /**
   * Check if JWT auth should be used (on known host with valid token)
   */
  shouldUseJwtAuth: (): boolean => {
    return jwtAuthStore.getState().shouldUseJwt;
  },

  /**
   * Clear JWT authentication
   */
  clearAuth: (): void => {
    jwtAuthStore.getState().clearJwtAuth();
  },
};

export default JwtAuthService;
